import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  CornerDownLeft,
  ChevronUp,
  ChevronDown,
  TrendingUp,
  Compass,
} from "lucide-react";
import { motion } from "framer-motion";
import useSiteSearch from "../hooks/useSiteSearch";
import { groupResults, metaFor } from "../utils/searchMeta";
import SearchDropdownResults from "./SearchDropdownResults";

// Desktop search — opens as a full-width sheet under the navbar, styled like
// the nav's mega-menu panels (same padding, category headings, dotted rows).
// Grouped, icon-led results; keyboard navigation (↑ ↓ ↵); an empty-state with
// quick links + popular searches. Mobile uses SearchBox.

// Shared horizontal padding: `bn-sheet-px` (Header.css) puts the content on
// the same left/right edge as the header bar while the sheet itself (and its
// borders) stays full width.
const SHEET_PX = "bn-sheet-px";

const QUICK_LINKS = [
  { label: "Products", url: "/products", type: "product" },
  { label: "AI Solutions", url: "/AI-Machine-Learning-Solution", type: "ai-solution" },
  { label: "Services", url: "/bestoservice", type: "service" },
  { label: "Industries", url: "/industry", type: "industry" },
  { label: "Insights", url: "/blogs", type: "blog" },
  { label: "About", url: "/about", type: "page" },
];

const POPULAR = ["SAP IBP", "Manufacturing", "Machine Learning", "Case Studies"];

export default function DesktopSearch({ onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { query, setQuery, results, total, suggestion, loading, error } = useSiteSearch({
    limit: 8,
  });
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmed = query.trim();
  const hasQuery = trimmed.length >= 2;
  const items = useMemo(() => results.slice(0, 8), [results]);
  // Flatten grouped items so the keyboard cursor follows visual order.
  const flatItems = useMemo(
    () => groupResults(items).flatMap((g) => g.items),
    [items]
  );

  useEffect(() => {
    const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  const go = useCallback(
    (url) => {
      setQuery("");
      if (onClose) onClose();
      navigate(url);
    },
    [navigate, onClose, setQuery]
  );

  const submit = useCallback(() => {
    if (!hasQuery) return;
    go(`/search?q=${encodeURIComponent(trimmed)}`);
  }, [hasQuery, trimmed, go]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatItems.length) setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flatItems.length)
        setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && flatItems[activeIndex]) go(flatItems[activeIndex].url);
      else submit();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="absolute left-0 right-0 top-full z-[70] bg-white border-t border-gray-100 overflow-hidden"
      style={{ boxShadow: "0 28px 50px -16px rgba(0,0,0,0.25)" }}
      role="dialog"
      aria-label="Site search"
    >
      {/* Input row */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className={`flex items-center gap-3.5 ${SHEET_PX} py-5 border-b border-gray-100`}
      >
        <Search size={24} strokeWidth={2.5} className="text-[#F26322] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search products, services, industries, insights…"
          aria-label="Search the site"
          className="flex-1 min-w-0 bg-transparent outline-none text-[17px] xl:text-[18px] text-gray-800 placeholder:text-gray-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current && inputRef.current.focus();
            }}
            aria-label="Clear search"
            className="grid place-items-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        )}
      </form>

      {/* Body */}
      <div className={`max-h-[62vh] overflow-y-auto ${SHEET_PX}`}>
        {hasQuery && loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-[#F26322] animate-spin" />
          </div>
        )}

        {/* Empty state: two mega-menu-style category columns — Quick Links
            (dotted rows, like the nav panels) and Popular Searches */}
        {!hasQuery && (
          <div
            className="grid gap-x-10 xl:gap-x-14 pt-6 pb-8"
            style={{ gridTemplateColumns: "repeat(2, minmax(0, 420px))" }}
          >
            <div>
              <div className="flex items-center gap-2.5 pb-3 border-b border-[#F26322]/30">
                <Compass size={20} className="shrink-0 text-[#F26322]" />
                <span className="text-[15px] xl:text-[16px] font-[600] uppercase tracking-wide text-[#F26322]">
                  Quick Links
                </span>
              </div>
              <ul className="list-none p-0 m-0">
                {QUICK_LINKS.map((q) => {
                  const { Icon } = metaFor(q.type);
                  return (
                    <li key={q.url} className="border-b border-dotted border-gray-300 last:border-b-0">
                      <button
                        onClick={() => go(q.url)}
                        className="group flex items-center gap-3 w-full text-left py-[12px] bg-transparent border-0"
                      >
                        <Icon size={17} className="shrink-0 text-[#F26322]/80" />
                        <span className="text-[15px] xl:text-[16px] text-[#212529] group-hover:text-[#F26322] transition-colors">
                          {q.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-2.5 pb-3 border-b border-[#F26322]/30">
                <TrendingUp size={20} className="shrink-0 text-[#F26322]" />
                <span className="text-[15px] xl:text-[16px] font-[600] uppercase tracking-wide text-[#F26322]">
                  Popular Searches
                </span>
              </div>
              <ul className="list-none p-0 m-0">
                {POPULAR.map((term) => (
                  <li key={term} className="border-b border-dotted border-gray-300 last:border-b-0">
                    <button
                      onClick={() => setQuery(term)}
                      className="group flex items-center gap-3 w-full text-left py-[12px] bg-transparent border-0"
                    >
                      <Search size={15} className="shrink-0 text-gray-400 group-hover:text-[#F26322] transition-colors" />
                      <span className="text-[15px] xl:text-[16px] text-[#212529] group-hover:text-[#F26322] transition-colors">
                        {term}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Request failed — not the same as "no matches" */}
        {hasQuery && !loading && error && (
          <div className="px-4 py-10 text-center">
            <p className="text-[14px] text-gray-600">
              Search is unavailable right now. Please try again.
            </p>
          </div>
        )}

        {/* No matches */}
        {hasQuery && !loading && !error && items.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-[14px] text-gray-600">
              No results for{" "}
              <span className="font-[600] text-gray-800">“{trimmed}”</span>
            </p>
            {suggestion && (
              <p className="mt-1.5 text-[13px] text-gray-500">
                Did you mean{" "}
                <button
                  onClick={() => setQuery(suggestion)}
                  className="text-[#F26322] font-[600] hover:underline"
                >
                  {suggestion}
                </button>
                ?
              </p>
            )}
          </div>
        )}

        {/* Results — width-capped for readability inside the wide sheet */}
        {hasQuery && !loading && items.length > 0 && (
          <div className="max-w-[900px] py-2">
            {suggestion && (
              <div className="px-4 py-2 text-[12px] text-gray-500 bg-gray-50/70 border-b border-gray-50">
                Did you mean{" "}
                <button
                  onClick={() => setQuery(suggestion)}
                  className="text-[#F26322] font-[600] hover:underline"
                >
                  {suggestion}
                </button>
                ?
              </div>
            )}
            <SearchDropdownResults
              results={items}
              query={trimmed}
              activeIndex={activeIndex}
              onSelect={go}
              onHover={setActiveIndex}
            />
          </div>
        )}
      </div>

      {/* Footer: result count + keyboard hints */}
      {hasQuery && !loading && items.length > 0 && (
        <div className={`flex items-center justify-between ${SHEET_PX} py-3 border-t border-gray-100 bg-gray-50/70`}>
          <button
            onClick={submit}
            className="text-[13.5px] font-[600] text-[#F26322] hover:underline"
          >
            View all {total} result{total === 1 ? "" : "s"}
          </button>
          <div className="hidden sm:flex items-center gap-3 text-[12px] text-gray-400">
            <span className="flex items-center gap-1">
              <ChevronUp size={12} className="inline" />
              <ChevronDown size={12} className="inline -ml-1" />
              navigate
            </span>
            <span className="flex items-center gap-1">
              <CornerDownLeft size={12} /> open
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}
